import React, { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { TrashIcon, PenIcon, MoreHorizontal, X } from 'lucide-react';
import { WithId } from '@schema/WithId.ts';
import { IconButton } from '@/components/ui/button.tsx';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover.tsx';
import { cn } from '@/lib/utils.ts';
import AnimatedPagination from '@/components/ui/pagination.tsx';
import { AnimatePresence, motion } from 'framer-motion';

export interface TableProps<T extends WithId & Record<string, unknown>> {
  data: T[];
  setDetailRow: (id: string) => void;
  detailRow: string | null;
  selectedRows: Set<string>;
  deleteSelectedRows: () => void;
  editRow: (id: string) => void;
  includedColumns?: string[];
  setSelectedRows: (rows: string[]) => void;
  columnNames?: string[];
  className?: string;
  /**
   * Custom renderers for specific columns.
   * Key is the column name, value is a function receiving (cellValue, rowData) and returning ReactNode.
   */
  cellRenderers?: Partial<{ [K in keyof T]: (value: T[K], row: T) => React.ReactNode }>;
  actualPage: number;
  totalPages: number;
  setPage: (page: number) => void;
  title?: string;
  subtitle?: string;
  emptyState?: React.ReactNode;
}

export default function ClickableTable<T extends WithId & Record<string, unknown>>({
  data,
  setDetailRow,
  detailRow,
  selectedRows,
  deleteSelectedRows,
  editRow,
  includedColumns,
  setSelectedRows,
  columnNames,
  className,
  cellRenderers,
  setPage,
  totalPages,
  actualPage,
  title,
  subtitle,
  emptyState,
}: TableProps<T>) {
  const [editMode, setEditMode] = useState(false);

  const columns = useMemo(() => {
    if (includedColumns && includedColumns.length > 0) return includedColumns;
    if (data.length > 0) return Object.keys(data[0]);
    return [] as string[];
  }, [includedColumns, data]);

  const columnSpan = Math.max(columns.length + 2, 1);

  const handleDeleteSelectedRows = (event: React.MouseEvent) => {
    event.stopPropagation();
    deleteSelectedRows();
  };

  const toggleRowSelection = (rowId: string) => {
    const current = Array.from(selectedRows);
    setSelectedRows(
      selectedRows.has(rowId) ? current.filter((id) => id !== rowId) : [...current, rowId],
    );
  };

  const toggleSelectAll = () => {
    setSelectedRows(selectedRows.size === data.length ? [] : data.map((row) => row.id));
  };

  const pageSummary = subtitle ??
    (data.length
      ? `Showing ${data.length} ${data.length === 1 ? 'entry' : 'entries'} on this page`
      : 'No data available');

  const selectionSummary = editMode
    ? selectedRows.size
      ? `${selectedRows.size} selected`
      : 'Select rows to delete'
    : undefined;

  return (
    <div
      className={cn(
        'surface-panel flex h-full min-h-[16rem] flex-col gap-4 p-3.5',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold tracking-tight">{title ?? 'Items'}</h3>
          <p className="text-xs text-white/50">{pageSummary}</p>
          {selectionSummary && <p className="text-xs uppercase tracking-[0.28em] text-white/45">{selectionSummary}</p>}
        </div>

        <TableActions
          editMode={editMode}
          setEditMode={setEditMode}
          deleteSelectedRows={handleDeleteSelectedRows}
          setSelectedRows={setSelectedRows}
          selectedCount={selectedRows.size}
        />
      </div>

      <div className="overflow-x-auto">
        <Table className="min-w-full border-separate border-spacing-y-2 text-sm">
          <TableHeader>
            <TableRow className="overflow-hidden rounded-lg border border-white/8 bg-content-dark/80">
              <TableHead className="w-12 rounded-l-2xl text-center align-middle">
                {editMode && (
                  <Checkbox checked={selectedRows.size === data.length && data.length > 0} onCheckedChange={toggleSelectAll} />
                )}
              </TableHead>
              {columns.map((col, index) => (
                <TableHead
                  key={col}
                  className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-white/45"
                >
                  {columnNames && index < columnNames.length ? columnNames[index] : col}
                </TableHead>
              ))}
              <TableHead className="w-12 rounded-r-2xl text-center align-middle" />
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnSpan} className="h-40 rounded-lg border border-dashed border-white/10 bg-content-dark/40 p-6 text-center align-middle">
                  {emptyState ?? (
                    <div className="text-sm text-white/60">No entries found. Try adjusting your filters.</div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              <AnimatePresence initial={false}>
                {data.map((row) => (
                  <motion.tr
                    key={row.id}
                    layout
                    initial={{ opacity: 0, y: 16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.97 }}
                    transition={{
                      opacity: { duration: 0.18, ease: 'easeOut' },
                      y: { duration: 0.24, ease: [0.18, 0.75, 0.25, 1] },
                      scale: { duration: 0.24, ease: [0.18, 0.75, 0.25, 1] },
                    }}
                    onClick={() => (!editMode ? setDetailRow(row.id) : toggleRowSelection(row.id))}
                    className={cn(
                      'group cursor-pointer overflow-hidden rounded-lg border border-white/10 bg-content/70 text-sm transition-colors duration-150 hover:border-white/15 hover:bg-content-highlight/70',
                      detailRow === row.id && !editMode && 'row-selected',
                      editMode && selectedRows.has(row.id) && 'border-primary/30 bg-primary/10',
                    )}
                  >
                    <TableCell className="w-12 rounded-l-2xl text-center align-middle">
                      {editMode && (
                        <Checkbox checked={selectedRows.has(row.id)} onCheckedChange={() => toggleRowSelection(row.id)} />
                      )}
                    </TableCell>
                    {columns.map((col) => {
                      const key = col as keyof T;
                      const value = row[key];
                      const renderer = cellRenderers?.[key];
                      return (
                        <TableCell key={`${row.id}_${String(col)}`} className="px-3 py-2 align-middle text-white/80">
                          {renderer ? renderer(value, row) : String(value ?? '')}
                        </TableCell>
                      );
                    })}
                    <TableCell className="w-12 rounded-r-2xl text-center align-middle">
                      <RowActions
                        rowId={row.id}
                        editRow={editRow}
                        deleteSelectedRows={handleDeleteSelectedRows}
                        setSelectedRows={setSelectedRows}
                      />
                    </TableCell>
                  </motion.tr>
                ))}
              </AnimatePresence>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs uppercase tracking-[0.3em] text-white/45">Page {actualPage + 1} of {Math.max(totalPages, 1)}</span>
        <AnimatedPagination actualPage={actualPage} setPage={setPage} totalPages={totalPages} />
      </div>
    </div>
  );
}

const TableActions = ({
  editMode,
  setEditMode,
  deleteSelectedRows,
  setSelectedRows,
  selectedCount,
}: {
  editMode: boolean;
  setEditMode: (value: boolean) => void;
  deleteSelectedRows: (event: React.MouseEvent) => void;
  setSelectedRows: (rows: string[]) => void;
  selectedCount: number;
}) => {
  const handleEditChange = (isEditingMode: boolean) => {
    setSelectedRows([]);
    setEditMode(isEditingMode);
  };

  return (
    <div className="flex items-center gap-2">
      {!editMode ? (
        <IconButton
          icon={PenIcon}
          onClick={() => handleEditChange(true)}
          variant="ghost-dark"
          size="icon"
          className="rounded-2xl border border-content-highlight/40 bg-content-dark/70"
        />
      ) : (
        <>
          <IconButton
            icon={TrashIcon}
            onClick={(e) => deleteSelectedRows(e)}
            variant="ghost-danger-glow"
            size="icon"
            disabled={selectedCount === 0}
            className="rounded-2xl border border-red-500/40 bg-red-500/20"
          />
          <IconButton
            icon={X}
            onClick={() => handleEditChange(false)}
            variant="ghost-light"
            size="icon"
            className="rounded-2xl border border-content-highlight/40 bg-content-dark/70"
          />
        </>
      )}
    </div>
  );
};

const RowActions = ({
  rowId,
  editRow,
  deleteSelectedRows,
  setSelectedRows,
}: {
  rowId: string;
  editRow: (id: string) => void;
  deleteSelectedRows: (event: React.MouseEvent) => void;
  setSelectedRows: (rows: string[]) => void;
}) => {
  const handleEditRow = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSelectedRows([rowId]);
    editRow(rowId);
  };

  const handleDeleteRow = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSelectedRows([rowId]);
    deleteSelectedRows(event);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton
          icon={MoreHorizontal}
          variant="ghost-light"
          size="icon"
          className="rounded-2xl border border-content-highlight/40 bg-content-dark/70"
          onClick={(event) => event.stopPropagation()}
        />
      </PopoverTrigger>
      <PopoverContent className="z-50 w-40 rounded-2xl border border-content-highlight/40 bg-content/90 p-2 text-white shadow-xl shadow-content-dark/40">
        <IconButton
          variant="ghost-dark"
          size="sm"
          className="w-full justify-start rounded-xl"
          onClick={(event) => handleEditRow(event)}
        >
          Edit
        </IconButton>
        <IconButton
          variant="ghost-danger-glow"
          size="sm"
          className="mt-1 w-full justify-start rounded-xl"
          onClick={(event) => handleDeleteRow(event)}
        >
          Delete
        </IconButton>
      </PopoverContent>
    </Popover>
  );
};
